/**
 * Pull the JSX of a single `<VariantBlock label="…">` out of a demo file's raw
 * source, so each example can show the exact snippet that produced *its*
 * preview — instead of one whole-file code blob for the whole component (which
 * leaves you guessing which code maps to which preview).
 *
 * Relies on two conventions that hold across every demo in `playground/src/demos`:
 *   - VariantBlocks are never nested, so the first `</VariantBlock>` after an
 *     opening tag is that block's closer.
 *   - Labels are written as `label="…"` with double quotes.
 *
 * Returns the dedented inner JSX, or `undefined` if the block can't be located
 * (callers fall back to a code-less preview).
 */
export function extractVariantSource(
  fullSource: string,
  label: string,
): string | undefined {
  const needle = `label="${label}"`;
  const labelIdx = fullSource.indexOf(needle);
  if (labelIdx === -1) return undefined;

  // End of the opening `<VariantBlock …>` tag — the first ">" at/after the
  // label attribute (tolerates a trailing `caption` attr or a line-wrapped tag).
  const openTagEnd = fullSource.indexOf(">", labelIdx);
  if (openTagEnd === -1) return undefined;

  // No nesting → the next close tag belongs to this block.
  const closeIdx = fullSource.indexOf("</VariantBlock>", openTagEnd);
  if (closeIdx === -1) return undefined;

  return dedent(fullSource.slice(openTagEnd + 1, closeIdx));
}

/** Strip common leading indentation and surrounding blank lines. */
function dedent(block: string): string {
  const lines = block.replace(/\t/g, "  ").split("\n");
  while (lines.length && lines[0].trim() === "") lines.shift();
  while (lines.length && lines[lines.length - 1]?.trim() === "") lines.pop();
  const indents = lines
    .filter((line) => line.trim() !== "")
    .map((line) => /^ */.exec(line)![0].length);
  const min = indents.length ? Math.min(...indents) : 0;
  return lines.map((line) => line.slice(min)).join("\n");
}
